import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useLiveAgentStore from '@/stores/liveAgent/liveAgentStore';
import { LiveAgentStepEnum } from '@/components/LiveAgentTopBar/LiveagentStepEnum';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import Button from '@/shared/components/UI/Button';
import DashboardIcon from '@/shared/icons/DashboardIcon';
import GraphIcon from '@/shared/icons/GraphIcon';
import SettingsIcon from '@/shared/icons/SettingsIcon';
import ZapIcon from '@/shared/icons/ZapIcon';
import LeftArrowIcon from '@/shared/icons/LeftArrowIcon';

const HumanHandoverSidebar = ({ closeMobileSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const setCurrentStep = useLiveAgentStore((state) => state.setCurrentStep);
  const { organization_id } = useParams();

  // Build nav links dynamically
  const buildNavLinks = () => {
    const baseNavLinks = [
      {
        id: LiveAgentStepEnum.OVERVIEW,
        label: 'Dashboard',
        icon: <DashboardIcon className="text-v3-purple-primary w-full h-full" />,
        iconPlacement: 'pre',
        active: location.pathname === `/human-handover/${organization_id}`,
        activeFunction: () => {
          return location.pathname === `/human-handover/${organization_id}`;
        },
        href: `/human-handover/${organization_id}`,
        onClick: () => {
          setCurrentStep(LiveAgentStepEnum.OVERVIEW);
          navigate(`/human-handover/${organization_id}`);
          closeMobileSidebar && closeMobileSidebar();
        },
      },
      {
        id: LiveAgentStepEnum.INSIGHTS,
        label: 'Insights',
        icon: <GraphIcon className="text-v3-purple-primary w-full h-full" />,
        iconPlacement: 'pre',
        active: location.pathname === `/human-handover/${organization_id}/insights`,
        activeFunction: () => {
          return location.pathname === `/human-handover/${organization_id}/insights`;
        },
        href: `/human-handover/${organization_id}/insights`,
        onClick: () => {
          setCurrentStep(LiveAgentStepEnum.INSIGHTS);
          navigate(`/human-handover/${organization_id}/insights`);
          closeMobileSidebar && closeMobileSidebar();
        },
      },
      {
        id: LiveAgentStepEnum.QUICK_RESPONSES,
        label: 'Quick Responses',
        icon: <ZapIcon className="text-v3-purple-primary w-full h-full" />,
        iconPlacement: 'pre',
        active: location.pathname === `/human-handover/${organization_id}/quick-responses`,
        activeFunction: () => {
          return location.pathname === `/human-handover/${organization_id}/quick-responses`;
        },
        href: `/human-handover/${organization_id}/quick-responses`,
        onClick: () => {
          setCurrentStep(LiveAgentStepEnum.QUICK_RESPONSES);
          navigate(`/human-handover/${organization_id}/quick-responses`);
          closeMobileSidebar && closeMobileSidebar();
        },
      },
      {
        id: LiveAgentStepEnum.SETTINGS,
        label: 'Settings',
        icon: <SettingsIcon className="text-v3-purple-primary w-full h-full" />,
        iconPlacement: 'pre',
        active: location.pathname === `/human-handover/${organization_id}/settings`,
        activeFunction: () => {
          return location.pathname === `/human-handover/${organization_id}/settings`;
        },
        href: `/human-handover/${organization_id}/settings`,
        onClick: () => {
          setCurrentStep(LiveAgentStepEnum.SETTINGS);
          navigate(`/human-handover/${organization_id}/settings`);
          closeMobileSidebar && closeMobileSidebar();
        },
      },
    ];
    return baseNavLinks;
  };

  const [navLinks, setNavLinks] = useState(buildNavLinks());

  // Update navLinks based on location changes
  useEffect(() => {
    const baseNavLinks = buildNavLinks();
    const updatedLinks = baseNavLinks.map((navLink) => {
      return {
        ...navLink,
        active: navLink.activeFunction(),
      };
    });
    setNavLinks(updatedLinks);
  }, [location.pathname, organization_id]);

  // NavItem component matching MainNav design
  const NavItem = ({ item, isActive, onClick }) => (
    <div
      className={`flex pl-[12px] pr-v3-sm py-[10px] h-[52px] md:h-[40px] lg:h-[40px] items-center gap-2 self-stretch rounded-v3-lg cursor-pointer hover:bg-v3-menu-hover ${
        isActive ? 'bg-v3-menu-selected' : ''
      }`}
      onClick={onClick}
    >
      <IconWrapper size={20}>
        {item.icon}
      </IconWrapper>
      <span className="text-v3-black-max text-center font-inter-variable text-v3-h10 font-medium whitespace-nowrap">
        {item.label}
      </span>
    </div>
  );

  return (
    <div className="chatbot_nav_wrapper flex flex-col gap-0 w-full md:w-[250px] h-full px-v3-sm pt-v3-sm md:pt-v3-xxs pb-v3-sm">
      {/* Workspace back button */}
      <div className="block px-[12px] py-[10px]">
        <Button
          icon={LeftArrowIcon}
          label="Workspace"
          onClick={() => navigate('/human-handover')}
          iconColor="!text-v3-purple-primary group-hover:!text-v3-purple-medium"
          className="group w-full justify-start !bg-transparent border-0 !text-v3-purple-primary !px-0 !py-0 h-[40px] !text-v3-h10 !font-[510] hover:!text-v3-purple-medium !gap-v3-xs"
        />
      </div>

      {/* Main navigation section */}
      <div className="flex flex-col gap-0 overflow-y-auto no-scrollbar flex-grow">
        {navLinks.map((item) => {
          const isActive = item.active;

          return (
            <div key={item.label} className="relative">
              <NavItem
                item={item}
                isActive={isActive}
                onClick={() => {
                  item.onClick?.();
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HumanHandoverSidebar;